<template>
  <div>
    <div class="setup-group-head advanced"><span>Advanced</span><span class="line"></span></div>

    <!-- Product & sample content -->
    <setup-collapse title="Product & sample content" :summary="productSummary" :done="productDone">
      <div class="field-row" style="margin-bottom: 16px;">
        <label class="field-label">Describe your product / service <span class="optional">(optional)</span></label>
        <div class="help-text" style="margin-bottom: 8px;">POP AI uses this to write more informed content about your product or service.</div>
        <textarea class="textarea" :maxlength="MAX_PRODUCT_DESCRIPTION" placeholder="Add description" v-model="advanced.product_service_description" @input="emit"></textarea>
        <div class="char-count">{{ (advanced.product_service_description || '').length }} / {{ MAX_PRODUCT_DESCRIPTION }}</div>
      </div>
      <div class="field-row">
        <label class="field-label">Provide sample content <span class="optional">(optional)</span></label>
        <div class="help-text" style="margin-bottom: 8px;">Give POP AI a piece of content to draw inspiration from.</div>
        <textarea class="textarea" :maxlength="MAX_SAMPLE_CONTENT" placeholder="Add sample content" v-model="advanced.sample_content" @input="emit"></textarea>
        <div class="char-count">{{ (advanced.sample_content || '').length }} / {{ MAX_SAMPLE_CONTENT }}</div>
      </div>
    </setup-collapse>

    <!-- Author & brand -->
    <setup-collapse title="Author & brand" :summary="authorSummary" :done="authorDone">
      <div class="field-grid-2">
        <div class="field-row">
          <label class="field-label">Author perspective</label>
          <input class="text-input" type="text" placeholder='e.g. "first person as a Manhattan dentist"' v-model="advanced.author_perspective" @input="emit" />
        </div>
        <div class="field-row">
          <label class="field-label">Brand name</label>
          <input class="text-input" type="text" placeholder="e.g. Italy on Foot - used naturally, not as spam" v-model="advanced.brand_name" @input="emit" />
        </div>
        <div class="field-row" style="grid-column: 1 / -1;">
          <label class="field-label">Target audience <span class="optional">(optional)</span></label>
          <textarea class="textarea" placeholder="e.g. First-time travelers to Italy who want practical, friendly advice" v-model="advanced.target_audience" @input="emit"></textarea>
        </div>
      </div>
    </setup-collapse>

    <!-- Words to avoid -->
    <setup-collapse title="Words to avoid" :summary="wordsSummary" :done="wordsDone">
      <div class="field-row" style="margin-bottom: 16px;">
        <div class="word-sub-header">Never use <v-tooltip top color="transparent">
          <template v-slot:activator="{ on, attrs }">
            <span class="ico" v-bind="attrs" v-on="on">i</span>
          </template>
          <div class="white-bg-tooltip small-tooltip">{{ NEVER_USE_TIP }}</div>
        </v-tooltip></div>
        <div class="word-tag-list">
          <span v-for="(w, i) in advanced.excluded_words.hard" :key="'h' + i" class="word-tag hard">{{ w }} <span class="x" @click="removeWord('hard', i)">×</span></span>
          <input class="add-word-input" placeholder="+ add banned word" v-model="newHard" @keyup.enter="addWord('hard')" />
        </div>
      </div>
      <div class="field-row">
        <div class="word-sub-header">Avoid if possible <v-tooltip top color="transparent">
          <template v-slot:activator="{ on, attrs }">
            <span class="ico" v-bind="attrs" v-on="on">i</span>
          </template>
          <div class="white-bg-tooltip small-tooltip">{{ AVOID_TIP }}</div>
        </v-tooltip></div>
        <div class="word-tag-list">
          <span v-for="(w, i) in advanced.excluded_words.soft" :key="'s' + i" class="word-tag soft">{{ w }} <span class="x" @click="removeWord('soft', i)">×</span></span>
          <input class="add-word-input" placeholder="+ add word to avoid" v-model="newSoft" @keyup.enter="addWord('soft')" />
        </div>
      </div>
      <div class="word-warn">
        <span class="ico">⚠</span>
        <span style="font-size: 0.75rem;"><b style="font-size: 0.75rem;">Tip:</b> very common words (like "section" or "page") can make writing hard. Add them to "Avoid if possible" instead of "Never use".</span>
      </div>
    </setup-collapse>

    <!-- Research & sources -->
    <setup-collapse title="Research & sources" :summary="researchSummary" done>
      <span class="field-label">Should POP back up facts with links to other websites?</span>
      <div class="help-text" style="margin-bottom: 12px;">When an article mentions prices, dates, rules, health or money, readers and Google trust it more if it shows where the information came from.</div>
      <div class="radio-list">
        <label
          v-for="o in WEB_SOURCE_OPTIONS"
          :key="String(o.value)"
          class="radio-row"
          :class="{ selected: !!advanced.use_web_sources === o.value }"
          @click="setWebSources(o.value)"
        >
          <span class="radio-dot"></span>
          <span><strong>{{ o.head }}</strong> {{ o.tail }}</span>
          <span class="research-cost" :class="{ free: !o.cost || ownKeySelected }">{{ researchCostLabel(o) }}</span>
          <span v-if="o.badge" class="recommend-badge">{{ o.badge }}</span>
        </label>
      </div>
    </setup-collapse>

    <!-- AI model & credits (design handoff: writer mockup › Setup) -->
    <setup-collapse title="AI model & credits" :summary="aiSummary" :done="aiDone">
      <span class="field-label">Who pays for the writing <v-tooltip top color="transparent">
        <template v-slot:activator="{ on, attrs }">
          <span class="info-ico" v-bind="attrs" v-on="on"></span>
        </template>
        <div class="white-bg-tooltip small-tooltip">{{ WHO_PAYS_TIP }}</div>
      </v-tooltip></span>
      <div class="help-text" style="margin-bottom: 10px;">With your own key, POP AI Writer reports cost <strong>2 POP credits flat</strong> instead of standard pricing (plus 1 credit per image if your Claude API key is enabled), and the AI company bills you for usage.</div>
      <div class="radio-list" style="margin-bottom: 16px;">
        <label
          v-for="a in API_MODES"
          :key="a.value"
          class="radio-row"
          :class="{ selected: advanced.api_mode === a.value }"
          @click="setApiMode(a.value)"
        >
          <span class="radio-dot"></span>
          <span><strong>{{ a.label }}</strong> {{ a.tail }}</span>
          <span v-if="a.badge" class="recommend-badge">{{ a.badge }}</span>
          <span v-if="a.provider" class="key-status" :class="{ on: isKeyInUse(a) }">
            {{ keyStatusLabel(a) }}
          </span>
        </label>
      </div>

      <!-- Provider key panel for the selected own-key mode (adds OR changes
           the ACCOUNT-level key — the same key Settings and every article use). -->
      <div v-if="selectedProvider" class="key-panel">
        <div class="kp-warn" v-if="!isProviderActive(selectedProvider)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.6L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>
          <template v-if="!hasProviderKey(selectedProvider)">
            <span v-if="isSubAccount">No {{ selectedProviderConfig.label }} key on this account yet. Ask the account owner to add one in Settings › AI access keys. Until then, this option can't write anything.</span>
            <span v-else>No {{ selectedProviderConfig.label }} key on your profile yet. <strong>Add one below</strong>. Until you do, this option can't write anything.</span>
          </template>
          <template v-else>
            <span v-if="isSubAccount">The account's {{ selectedProviderConfig.label }} key is saved but turned off. Ask the account owner to turn it on in Settings › AI access keys to use it for this article.</span>
            <span v-else>Your {{ selectedProviderConfig.label }} key is saved but turned off. Turn it on in <a v-if="openAiKeyLink" :href="openAiKeyLink" target="_blank" rel="noopener"><strong>Settings › AI access keys</strong></a><strong v-else>Settings › AI access keys</strong> to use it for this article.</span>
          </template>
        </div>
        <div class="kp-head">
          <span class="kp-logo" :style="{ background: selectedProviderConfig.logoColor }">{{ selectedProviderConfig.logo }}</span>
          <span class="kp-title">{{ selectedProviderConfig.panelTitle }}</span>
          <span class="kp-hint" v-if="storedKeyTail">Ends in {{ storedKeyTail }}</span>
        </div>
        <template v-if="!isSubAccount">
          <div class="kp-row">
            <input class="text-input" :type="revealKey ? 'text' : 'password'" spellcheck="false" autocomplete="off"
              :placeholder="selectedProviderConfig.placeholder"
              v-model="inlineKey" @input="kpMsg = null" />
            <button type="button" class="kp-btn" @click="revealKey = !revealKey">{{ revealKey ? 'Hide' : 'Show' }}</button>
            <button type="button" class="kp-btn" :disabled="!inlineKey.trim() || kpTesting" @click="testInlineKey">Test key</button>
            <button type="button" class="kp-btn primary" :disabled="!inlineKeyDirty || kpSaving" @click="saveInlineKey">{{ saveKeyLabel }}</button>
          </div>
          <div class="kp-msg" :class="kpMsg.kind" v-if="kpMsg">
            <svg v-if="kpMsg.kind === 'ok'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"></path></svg>
            <svg v-else-if="kpMsg.kind === 'busy'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.2-8.6"></path></svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 8v4"></path><path d="M12 16h.01"></path></svg>
            <span v-html="kpMsg.html"></span>
          </div>
          <div class="help-text">{{ selectedProviderConfig.help }}</div>
        </template>
      </div>

      <!-- Which model writes the article (account-level, read-only here) -->
      <span class="field-label">Which model writes the article <v-tooltip top color="transparent">
        <template v-slot:activator="{ on, attrs }">
          <span class="info-ico" v-bind="attrs" v-on="on"></span>
        </template>
        <div class="white-bg-tooltip small-tooltip">{{ WHICH_MODEL_TIP }}</div>
      </v-tooltip></span>
      <div class="help-text">Set once for the whole account in <a v-if="openAiKeyLink" :href="openAiKeyLink" target="_blank" rel="noopener"><strong>Settings › AI access keys</strong></a><strong v-else>Settings › AI access keys</strong>, so every article writes with the same model. POP keeps that list current, so when a provider ships a new model it takes over the role it fits.</div>
    </setup-collapse>
  </div>
</template>

<script>
import SetupCollapse from "./SetupCollapse.vue";
import { MAX_PRODUCT_DESCRIPTION, MAX_SAMPLE_CONTENT } from "../../constants";

const RESEARCH_LEVELS = [
  { value: "none", label: "No extra research - use only the POP brief and your inputs.", badge: "" },
  { value: "light", label: "Light source support - authoritative sources for important factual claims.", badge: "Recommended" },
  { value: "verified", label: "Verified sources required - require citations for every factual claim.", badge: "" },
];
// The programmatic mode values are 'pop' | 'own_key' (the user's OpenAI key —
// legacy name kept for stored setups) | 'anthropic_key' (the user's Claude
// key). Display copy says "Claude"; the code token stays 'anthropic'.
const API_MODES = [
  { value: "pop", label: "Use POP credits.", tail: "Nothing to set up. Any model below is available.", badge: "Recommended", provider: null },
  { value: "own_key", label: "Use my OpenAI key.", tail: "GPT models only.", badge: "", provider: "openai" },
  { value: "anthropic_key", label: "Use my Claude key.", tail: "Claude models only.", badge: "", provider: "anthropic" },
];
const OWN_KEY_MODES = ["own_key", "anthropic_key"];
// Provider metadata for the key panel (design handoff's `meta` table).
const PROVIDER_CONFIG = {
  openai: {
    logo: "AI", logoColor: "#10a37f",
    label: "OpenAI", family: "GPT",
    panelTitle: "OpenAI API key",
    placeholder: "sk-…", prefix: "sk-", prefixLabel: "sk-",
    reject: "sk-ant-", rejectLabel: "Claude",
    help: "Paste the key from your OpenAI dashboard. It is stored on your POP profile and used for every article where you pick this option.",
    userKeyField: "openAIKey", preferredField: "preferredOpenaiModel",
  },
  anthropic: {
    logo: "CL", logoColor: "#D97757",
    label: "Claude", family: "Claude",
    panelTitle: "Claude (Anthropic) API key",
    placeholder: "sk-ant-…", prefix: "sk-ant-", prefixLabel: "sk-ant-",
    reject: null, rejectLabel: "",
    help: "Paste the key from console.anthropic.com. It is stored on your POP profile and used for every article where you pick this option.",
    userKeyField: "anthropicKey", preferredField: "preferredAnthropicModel",
  },
};
const WHO_PAYS_TIP =
  "POP credits are the simple option: nothing to set up and one bill. If you add your own OpenAI or Claude key, the AI company bills you directly for the words it writes, and POP only charges a flat 2 credits per article.";
const WHICH_MODEL_TIP =
  "Model choice is an account-level setting so every article in the account writes with the same model. Change it in Settings › AI access keys.";
// Opt-in "authoritative web sources" (boolean use_web_sources). Off = no links;
// on = POP web-searches, cites the sources inline and lists them at the end (+1 credit).
const WEB_SOURCE_OPTIONS = [
  {
    value: false,
    head: "No extra research.",
    tail: "POP writes from your brief only. No links to other websites.",
    cost: 0,
    badge: "",
  },
  {
    value: true,
    head: "Source support.",
    tail: "POP finds up to five trustworthy websites, adds a small numbered link like [1] wherever your article states a fact, and lists the websites at the end so readers can check them.",
    cost: 1,
    badge: "Recommended",
  },
];
// Info-tooltip copy (design handoff).
const NEVER_USE_TIP =
  "A hard no. POP will never put these words in your article. Use it for competitor names, banned claims, or wording your brand doesn't allow.";
const AVOID_TIP =
  "A soft no. POP steers around these words but may still use one if the sentence would otherwise read badly. Use the “Never use” list above for words that must never appear.";

export default {
  name: "AiwV2AdvancedSection",
  components: { SetupCollapse },
  props: { model: { type: Object, required: true } },
  data() {
    return {
      MAX_PRODUCT_DESCRIPTION, MAX_SAMPLE_CONTENT, RESEARCH_LEVELS, WEB_SOURCE_OPTIONS, API_MODES,
      NEVER_USE_TIP, AVOID_TIP, WHO_PAYS_TIP, WHICH_MODEL_TIP,
      newHard: "", newSoft: "",
      // Provider key panel (design handoff state): password-masked by default.
      inlineKey: "",
      revealKey: false,
      kpMsg: null, // { kind: 'ok' | 'err' | 'busy', html }
      kpTesting: false,
      kpSaving: false,
    };
  },
  computed: {
    advanced() {
      return this.model.advanced;
    },
    // Deep-link to the "AI access keys" tab in account settings (opens in a
    // new tab so the in-progress AI Writer setup isn't lost).
    openAiKeyLink() {
      const id = this.$store.state.user && this.$store.state.user.id;
      if (!id) return null;
      return this.$router.resolve({
        name: "UserProfile",
        params: { userId: id },
        query: { mainTab: "ai-access-keys" },
      }).href;
    },
    // The run's owner: the agency account in sub-account context (keys arrive
    // as booleans there), else the logged-in user.
    ownerUser() {
      return this.$store.state.selectedSubAccountRelation || this.$store.state.user || {};
    },
    isSubAccount() {
      return !!this.$store.state.selectedSubAccountRelation;
    },
    selectedProvider() {
      const mode = API_MODES.find((a) => a.value === this.advanced.api_mode);
      return (mode && mode.provider) || null;
    },
    selectedProviderConfig() {
      return this.selectedProvider ? PROVIDER_CONFIG[this.selectedProvider] : null;
    },
    // Design handoff's syncSaveBtn: dirty = non-empty AND different from stored.
    inlineKeyDirty() {
      if (!this.selectedProvider) return false;
      const v = (this.inlineKey || "").trim();
      return v !== "" && v !== this.storedKeyFor(this.selectedProvider);
    },
    saveKeyLabel() {
      if (this.selectedProvider && this.hasProviderKey(this.selectedProvider) && this.inlineKeyDirty) {
        return "Replace key";
      }
      return "Save key";
    },
    // "Ends in XXXX" hint for a stored key (raw string only — sub-accounts get
    // booleans and no tail).
    storedKeyTail() {
      if (!this.selectedProvider) return "";
      const stored = this.storedKeyFor(this.selectedProvider);
      return stored ? stored.slice(-4) : "";
    },
    productSummary() {
      return this.advanced.product_service_description || this.advanced.sample_content ? "Description provided" : "No description provided";
    },
    productDone() {
      return !!(this.advanced.product_service_description || this.advanced.sample_content);
    },
    authorSummary() {
      const parts = [];
      if (this.advanced.author_perspective) parts.push(this.advanced.author_perspective);
      if (this.advanced.brand_name) parts.push(this.advanced.brand_name);
      return parts.join(" · ") || "Not set";
    },
    authorDone() {
      return !!(this.advanced.author_perspective || this.advanced.brand_name || this.advanced.target_audience);
    },
    wordsSummary() {
      const hard = this.advanced.excluded_words.hard.length;
      const soft = this.advanced.excluded_words.soft.length;
      return `${hard} banned · ${soft} to avoid`;
    },
    wordsDone() {
      return this.advanced.excluded_words.hard.length > 0 || this.advanced.excluded_words.soft.length > 0;
    },
    researchSummary() {
      return this.advanced.use_web_sources ? "Source support · POP finds up to 5" : "No extra research";
    },
    // Own-key runs pay the flat own-key rate; the sources pass is included.
    ownKeySelected() {
      return OWN_KEY_MODES.includes(this.advanced.api_mode);
    },
    // Design handoff's syncNote, extended: a missing OR paused key blocks.
    aiSummary() {
      if (!this.selectedProvider) return "POP credits";
      const label = PROVIDER_CONFIG[this.selectedProvider].label;
      if (!this.hasProviderKey(this.selectedProvider)) return `${label} key needed`;
      if (!this.isProviderActive(this.selectedProvider)) return `${label} key paused`;
      return `Own key · ${label}`;
    },
    aiDone() {
      return !this.selectedProvider || this.isProviderActive(this.selectedProvider);
    },
  },
  methods: {
    emit() {
      this.$emit("change");
    },
    addWord(kind) {
      const field = kind === "hard" ? "newHard" : "newSoft";
      // A pasted list ("embark, delve; leverage") adds each entry as its own
      // chip — split on commas/semicolons/newlines, skipping duplicates.
      const words = (this[field] || "")
        .split(/[,;\n]+/)
        .map((w) => w.trim())
        .filter(Boolean);
      if (!words.length) return;
      const list = this.advanced.excluded_words[kind];
      words.forEach((w) => {
        if (!list.some((x) => String(x).toLowerCase() === w.toLowerCase())) list.push(w);
      });
      this[field] = "";
      this.emit();
    },
    removeWord(kind, idx) {
      this.advanced.excluded_words[kind].splice(idx, 1);
      this.emit();
    },
    setWebSources(value) {
      this.advanced.use_web_sources = value;
      this.emit();
    },
    researchCostLabel(o) {
      if (!o.cost) return "Default";
      return this.ownKeySelected ? "Included" : "1 credit";
    },
    setResearch(value) {
      this.advanced.research_level = value;
      this.emit();
    },
    // Split a "Head - description" option label so the head renders bold (design).
    labelParts(label) {
      const s = String(label || "");
      const i = s.indexOf(" - ");
      if (i === -1) return { head: s, tail: "" };
      return { head: s.slice(0, i).trim(), tail: " " + s.slice(i).trim() };
    },
    setApiMode(value) {
      this.advanced.api_mode = value;
      this.emit();
    },
    storedKeyFor(provider) {
      const value = this.ownerUser[PROVIDER_CONFIG[provider].userKeyField];
      // Sub-accounts receive booleans, never the raw owner key.
      return typeof value === "string" ? value : "";
    },
    hasProviderKey(provider) {
      return !!this.ownerUser[PROVIDER_CONFIG[provider].userKeyField];
    },
    // ACTIVE = the account's own-key toggle is on for THIS provider. A key
    // that is merely stored ("Saved, paused" in Settings) can't write articles.
    isProviderActive(provider) {
      return !!(this.ownerUser.useMyOwnApiKey
        && (this.ownerUser.ownApiKeyProvider || "openai") === provider
        && this.hasProviderKey(provider));
    },
    // Row badge. "In use" only when THIS row is the run's selection AND the
    // account key is on: the account toggle alone used to read as "this
    // article uses my key" while the run sat in POP mode and the preview
    // showed POP credits (SUP-0030). "Ready" = key on, not picked for this
    // article.
    isKeyInUse(mode) {
      return this.advanced.api_mode === mode.value && this.isProviderActive(mode.provider);
    },
    keyStatusLabel(mode) {
      if (!this.hasProviderKey(mode.provider)) return "Not added";
      if (!this.isProviderActive(mode.provider)) return "Paused";
      return this.advanced.api_mode === mode.value ? "In use" : "Ready";
    },
    syncInlineKey() {
      this.kpMsg = null;
      this.revealKey = false;
      this.inlineKey = this.selectedProvider ? this.storedKeyFor(this.selectedProvider) : "";
    },
    reloadUser() {
      return this.$store.dispatch("loadUser").then((response) => {
        if (response && response.data && response.data.user) {
          this.$store.commit("setUser", response.data.user);
        }
        this.syncInlineKey();
      });
    },
    // Design handoff's keyProblem: both providers issue keys beginning "sk-",
    // so a bare prefix check lets a Claude key pass as an OpenAI one — reject
    // the other provider's namespace first. Returns an HTML snippet or null.
    keyProblem(provider, v) {
      const m = PROVIDER_CONFIG[provider];
      if (m.reject && v.startsWith(m.reject)) {
        return "That looks like a " + m.rejectLabel + " key, not " + m.label + ". Keys from both start with <strong>sk-</strong>, so they are easy to mix up. Check which dashboard you copied from.";
      }
      if (!v.startsWith(m.prefix)) {
        return m.label + " keys start with <strong>" + m.prefixLabel + "</strong>. Check you copied the whole key from the right dashboard.";
      }
      return null;
    },
    testInlineKey() {
      const provider = this.selectedProvider;
      const v = (this.inlineKey || "").trim();
      if (!provider || !v || this.kpTesting) return;
      const m = PROVIDER_CONFIG[provider];
      const bad = this.keyProblem(provider, v);
      if (bad) { this.kpMsg = { kind: "err", html: bad }; return; }
      this.kpMsg = { kind: "busy", html: "Checking the key with " + m.label + "…" };
      this.kpTesting = true;
      this.$store.dispatch("testAiKey", { provider, apiKey: v })
        .then((response) => {
          const ok = response && response.data && response.data.status === "SUCCESS";
          if (ok) {
            const nothingToSave = this.hasProviderKey(provider) && v === this.storedKeyFor(provider);
            this.kpMsg = { kind: "ok", html: "Key works. " + m.label + " accepted it" + (nothingToSave ? ". Nothing to save." : ". Save it to start using it.") };
          } else {
            this.kpMsg = { kind: "err", html: "POP couldn't use this key. Check you copied the whole thing, then try again." };
          }
        })
        .catch(() => {
          this.kpMsg = { kind: "err", html: "POP couldn't use this key. Check you copied the whole thing, then try again." };
        })
        .finally(() => { this.kpTesting = false; });
    },
    // Saves the ACCOUNT-level key (same key as Settings › AI access keys).
    saveInlineKey() {
      const provider = this.selectedProvider;
      const v = (this.inlineKey || "").trim();
      if (!provider || !v || this.kpSaving) return;
      const bad = this.keyProblem(provider, v);
      if (bad) { this.kpMsg = { kind: "err", html: bad }; return; }
      this.kpMsg = { kind: "busy", html: "Saving the key…" };
      this.kpSaving = true;
      this.$store.dispatch("manageAiKey", { provider, apiKey: v })
        .then((response) => {
          const ok = response && response.data && response.data.status === "SUCCESS";
          if (ok) {
            return this.reloadUser().then(() => {
              this.kpMsg = { kind: "ok", html: "Key saved. This option is now in use for every article where you pick it." };
            });
          }
          this.kpMsg = { kind: "err", html: (response && response.data && response.data.error) || "Failed to save the key. Please try again." };
        })
        .catch(() => {
          this.kpMsg = { kind: "err", html: "Failed to save the key. Please try again." };
        })
        .finally(() => { this.kpSaving = false; });
    },
  },
  watch: {
    selectedProvider: {
      immediate: true,
      handler() {
        this.syncInlineKey();
      },
    },
  },
};
</script>

<style scoped>
/* Research & sources cost pill (design handoff): orange "1 credit", green "Default". */
.research-cost {
  margin-left: auto;
  flex: none;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.03em;
  padding: 3px 9px;
  border-radius: 100px;
  white-space: nowrap;
  background: var(--pop-orange-soft, #ffeddd);
  color: var(--pop-orange-deep, #b23d00);
}
.research-cost.free {
  background: var(--pop-green-soft, #e0f5ec);
  color: var(--pop-green, #25a97a);
}

/* Match the design handoff: small, uppercase, muted sub-headers for the
   "Never use" / "Avoid if possible" word lists. */
.word-sub-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: var(--text-3, #6b7b7e);
  margin-bottom: 8px;
}
.word-sub-header .ico {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #014751;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  cursor: pointer;
}
/* Keep emphasized help text (e.g. "2 POP credits flat") in the muted color;
   Bulma's global `strong` rule would otherwise darken it to #363636. */
.help-text strong { color: inherit; font-weight: 700; }

/* ---- AI model & credits (ported verbatim from the writer design handoff) -- */

/* Field-label info icon (same masked glyph as Hero.vue / the handoff). */
.info-ico {
  width: 15px; height: 15px;
  display: inline-block; flex-shrink: 0;
  vertical-align: -2px; cursor: pointer; user-select: none;
  background-color: #014751;
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat;
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat;
}

/* "In use / Ready / Paused / Not added" chip on the own-key mode rows; green
   only for the row the run actually bills. */
.key-status {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .03em;
  text-transform: uppercase;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--surface-2, #f7f7f7);
  color: var(--text-3, #6b7b7e);
  border: 1px solid var(--border, #e4e4e4);
  flex-shrink: 0;
}
.key-status.on { background: var(--pop-green-soft, #eafbf3); color: #0F7355; border-color: transparent; }

/* Provider key panel (Setup › AI model & credits). */
.key-panel { border: 1px solid var(--border-strong, #ccc); border-radius: 9px; padding: 13px 14px; background: var(--surface-2, #f7f7f7); margin-bottom: 16px; }
.key-panel .kp-head { display: flex; align-items: center; gap: 9px; margin-bottom: 10px; }
.key-panel .kp-logo { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; color: #fff; background: var(--text, #052e33); flex: none; letter-spacing: -.2px; }
.key-panel .kp-title { font-size: 13.5px; font-weight: 700; color: var(--text, #052e33); }
.key-panel .kp-hint { margin-left: auto; font-size: 12px; color: var(--text-3, #6b7b7e); font-variant-numeric: tabular-nums; flex-shrink: 0; }
.key-panel .kp-row { display: flex; align-items: center; gap: 7px; }
.key-panel .kp-row .text-input { flex: 1; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px; letter-spacing: .02em; }
.kp-msg { display: flex; align-items: flex-start; gap: 7px; font-size: 12.5px; font-weight: 600; margin-top: 9px; line-height: 1.45; }
.kp-msg svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 2px; }
.kp-msg.err { color: #C0392B; }
.kp-msg.ok { color: #0F7355; }
.kp-msg.busy { color: var(--text-3, #6b7b7e); }
.kp-msg strong { color: inherit; font-weight: 700; }
.kp-warn { display: flex; align-items: flex-start; gap: 9px; background: var(--pop-orange-soft, #ffeddd); border: 1px solid #FFC9A6; border-radius: 8px; padding: 10px 12px; margin-bottom: 12px; font-size: 13px; color: #8A3B12; line-height: 1.5; }
.kp-warn svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; color: var(--pop-orange, #ff5700); }
.kp-warn strong { color: #6E2E0C; font-weight: 700; }

/* Key-panel buttons (design's .btn .btn-soft/.btn-primary .btn-sm pairing). */
.kp-btn {
  font-family: inherit;
  flex: none;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 7px;
  font-weight: 600;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all .15s;
  background: #fff;
  color: var(--text, #052e33);
  border: 1px solid var(--border-strong, #ccc);
}
.kp-btn:hover { background: var(--surface-2, #f7f7f7); }
.kp-btn.primary {
  background: var(--pop-orange, #ff5700);
  color: #fff;
  border: none;
  box-shadow: 0 2px 6px rgba(255,87,0,.25);
}
.kp-btn.primary:hover { background: var(--pop-orange-hover, #e64e00); }
.kp-btn:disabled { opacity: .55; cursor: not-allowed; }
.kp-btn.primary:disabled { background: var(--border-strong, #ccc); color: var(--text-muted, #9aa6a8); box-shadow: none; }
</style>
